import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats } from '@angular/material/core';
import { MatCalendar, MatDatepickerIntl } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

/**
 * Shared custom calendar header used by every date picker in the app.
 *
 * Renders the Figma-spec period label and chevron nav buttons. The nav
 * buttons opt into the shared white-chip tooltip (`.white-tooltip`) which the
 * default Material header cannot do, since its internal tooltips are dark-only.
 *
 * Standalone so any module can reference the class for `[calendarHeaderComponent]`
 * without declaring it — Material instantiates it dynamically via ComponentPortal.
 */
@Component({
  selector: 'app-calendar-header',
  templateUrl: './calendar-header.component.html',
  styleUrls: ['./calendar-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatTooltipModule],
})
export class CalendarHeaderComponent<D> implements OnDestroy {
  private readonly destroyed = new Subject<void>();

  constructor(
    private intl: MatDatepickerIntl,
    public calendar: MatCalendar<D>,
    private dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private dateFormats: MatDateFormats,
    private cdr: ChangeDetectorRef
  ) {
    // The header may need to update its labels whenever the calendar state changes.
    this.calendar.stateChanges.pipe(takeUntil(this.destroyed)).subscribe(() => this.cdr.markForCheck());
  }

  get periodLabel(): string {
    if (this.calendar.currentView === 'month') {
      const raw = this.dateAdapter.format(this.calendar.activeDate, this.dateFormats.display.monthYearLabel);
      // Preserve the existing title-case behaviour (e.g. "July 2020").
      return this.toTitleCase(raw);
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYearName(this.calendar.activeDate);
    }
    // multi-year: show the year range covered by the current page.
    const activeYear = this.dateAdapter.getYear(this.calendar.activeDate);
    const firstYearInView = activeYear - (activeYear % yearsPerPage);
    const lastYearInView = firstYearInView + yearsPerPage - 1;
    return `${firstYearInView} – ${lastYearInView}`;
  }

  get prevButtonLabel(): string {
    return this.intl.prevMonthLabel;
  }

  get nextButtonLabel(): string {
    return this.intl.nextMonthLabel;
  }

  get periodButtonLabel(): string {
    return this.intl.switchToMultiYearViewLabel;
  }

  ngOnDestroy(): void {
    this.destroyed.next();
    this.destroyed.complete();
  }

  /** Toggles the multi-year view, matching the default period-button behaviour. */
  currentPeriodClicked(): void {
    this.calendar.currentView = this.calendar.currentView === 'month' ? 'multi-year' : 'month';
  }

  previousClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, -1)
        : this.dateAdapter.addCalendarYears(
            this.calendar.activeDate,
            this.calendar.currentView === 'year' ? -1 : -yearsPerPage
          );
  }

  nextClicked(): void {
    this.calendar.activeDate =
      this.calendar.currentView === 'month'
        ? this.dateAdapter.addCalendarMonths(this.calendar.activeDate, 1)
        : this.dateAdapter.addCalendarYears(
            this.calendar.activeDate,
            this.calendar.currentView === 'year' ? 1 : yearsPerPage
          );
  }

  previousEnabled(): boolean {
    if (!this.calendar.minDate) {
      return true;
    }
    return !this.isSameView(this.calendar.activeDate, this.calendar.minDate);
  }

  nextEnabled(): boolean {
    return !this.calendar.maxDate || !this.isSameView(this.calendar.activeDate, this.calendar.maxDate);
  }

  private isSameView(date1: D, date2: D): boolean {
    if (this.calendar.currentView === 'month') {
      return (
        this.dateAdapter.getYear(date1) === this.dateAdapter.getYear(date2) &&
        this.dateAdapter.getMonth(date1) === this.dateAdapter.getMonth(date2)
      );
    }
    if (this.calendar.currentView === 'year') {
      return this.dateAdapter.getYear(date1) === this.dateAdapter.getYear(date2);
    }
    // multi-year
    return (
      Math.floor(this.dateAdapter.getYear(date1) / yearsPerPage) ===
      Math.floor(this.dateAdapter.getYear(date2) / yearsPerPage)
    );
  }

  private toTitleCase(value: string): string {
    if (!value) {
      return value;
    }
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }
}

// Number of years displayed per multi-year page (matches Material's constant).
const yearsPerPage = 24;
